import type { Nutrients } from '@nutrigeist/shared';
import { formatGrams } from '../lib/format.js';

/**
 * The nutrients the app already records but never showed: sugar, saturated fat,
 * fibre and salt. Everything but fibre is a "less is better" reference; fibre is
 * a target to reach. Values are the EU reference intakes for an adult (fibre
 * from the DGE) — not personalised goals, so they are framed as guidance.
 */
interface Ref {
  key: keyof Nutrients;
  label: string;
  reference: number;
  /** 'target' fills toward the value (more is better); 'limit' warns when over. */
  kind: 'target' | 'limit';
  color: string;
}

const REFERENCES: Ref[] = [
  { key: 'fiber', label: 'Ballaststoffe', reference: 30, kind: 'target', color: 'var(--accent)' },
  { key: 'sugars', label: 'Zucker', reference: 90, kind: 'limit', color: 'var(--carbs)' },
  { key: 'saturated', label: 'Ges. Fett', reference: 20, kind: 'limit', color: 'var(--fat)' },
  { key: 'salt', label: 'Salz', reference: 6, kind: 'limit', color: 'var(--protein)' },
];

export function NutrientDetails({
  totals,
  fiberGoal = null,
  fiberGaps = 0,
}: {
  totals: Nutrients;
  /** The user's own fibre target; null falls back to the DGE reference. */
  fiberGoal?: number | null;
  /** Entries whose food declares no fibre at all; their fibre counts as zero. */
  fiberGaps?: number;
}): JSX.Element {
  return (
    <div className="card">
      <div className="card-title">Weitere Nährwerte</div>

      <div className="macro-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
        {REFERENCES.map(({ key, label, reference, kind, color }) => {
          const value = totals[key];
          // Fibre is the one the user can set for themselves; the rest stay on
          // the published reference intakes.
          const target =
            key === 'fiber' && fiberGoal != null && fiberGoal > 0 ? fiberGoal : reference;
          const percent = Math.min(100, (value / target) * 100);
          const over = kind === 'limit' && value > target;
          const isOwnGoal = key === 'fiber' && fiberGoal != null && fiberGoal > 0;

          return (
            <div className="macro-cell" key={key}>
              <div className="macro-value">{formatGrams(value)} g</div>
              <div className="macro-label">{label}</div>
              <div className="macro-bar">
                <span
                  style={{ width: `${percent}%`, background: over ? 'var(--danger)' : color }}
                />
              </div>
              <div className="hint" style={{ marginTop: 4 }}>
                {kind === 'target'
                  ? `Ziel ${formatGrams(target)} g${isOwnGoal ? '' : ' (DGE)'}`
                  : `Richtwert ${target} g`}
              </div>
            </div>
          );
        })}
      </div>

      {/* Absent data counts as zero, so a low fibre total can mean "ate little
          fibre" or "the products never declared it". Those are different facts
          and the difference has to be visible. */}
      {fiberGaps > 0 && (
        <div className="hint" style={{ marginTop: 10 }}>
          Bei {fiberGaps} {fiberGaps === 1 ? 'Eintrag' : 'Einträgen'} sind keine Ballaststoffe
          angegeben — der tatsächliche Wert liegt höher.
        </div>
      )}

      <div className="hint" style={{ marginTop: 10 }}>
        Richtwerte: EU-Referenzmengen für Erwachsene, Ballaststoffe nach DGE — keine persönlichen
        Ziele.
      </div>
    </div>
  );
}
